// API Types - Sync with receptari/api/model/recipe.py

export interface Image {
  id: string;
  url: string;
  isDefault: boolean;
  createdAt?: string;
}

export interface Quantity {
  amount: number;
  unit: string;
}

// Well-known built-in unit IDs
const BUILTIN_KILOS = 'builtin_kilos' as const;
const BUILTIN_LITERS = 'builtin_liters' as const;
const BUILTIN_UNIT = 'builtin_units' as const;

export const BUILTIN_UNIT_IDS = {
  KILOS: BUILTIN_KILOS,
  LITERS: BUILTIN_LITERS,
  UNIT: BUILTIN_UNIT,
} as const;

export interface Tag {
  id: string;
  name: string;
  /** A key into lib/tags/tagColors.ts's 16-color palette. */
  color: string;
}

export interface TagUpdate {
  name?: string;
  color?: string;
}

/** A directed "implies" edge — assigning fromTagId to an ingredient also
 *  implies toTagId (and, transitively, whatever that implies). Storage
 *  stays normalized (only explicit tags are ever written to an
 *  ingredient); implied tags are computed client-side from these edges —
 *  see lib/tags/tagGraph.ts. */
export interface TagImplication {
  fromTagId: string;
  toTagId: string;
}

/** A tag plus how many distinct ingredients carry it — explorer table rows. */
export interface TagWithUsage {
  tag: Tag;
  ingredientCount: number;
}

export interface Ingredient {
  id: string;
  name: string;
  description?: string;
  images?: Image[];
  tags?: Tag[];
}

export interface IngredientInfo {
  ingredient: Ingredient;
  description?: string;
  quantity?: Quantity;
}

/** An ingredient plus how many distinct recipes use it — explorer table rows. */
export interface IngredientWithUsage {
  ingredient: Ingredient;
  recipeCount: number;
}

export interface Transformation {
  id: string;
  name: string;
  description?: string;
  images?: Image[];
  renderTemplate?: string;
  /** Seconds. Seeds a new usage's own `duration` — see time_units.ts. */
  typicalDuration?: number;
}

export interface TransformationInfo {
  transformation?: Transformation;
  description?: string;
  parameters?: Record<string, unknown>;
  /** Seconds — shown in step text as "durant X". */
  duration?: number;
  /** Seconds, always <= duration — how much of it needs the preparer's
   *  hands before they're free to start the next step. Unset means the
   *  whole duration does (not parallelizable) — see schedule.ts. */
  attentionDuration?: number;
}

/** Symmetric with IngredientWithUsage. */
export interface TransformationWithUsage {
  transformation: Transformation;
  recipeCount: number;
}

export interface Tool {
  id: string;
  name: string;
  description?: string;
  images?: Image[];
  defaultHidden?: boolean;
  defaultCounts?: boolean;
}

/**
 * One tool usage on a node — a node can use several at once (e.g. a pot
 * and a handheld blender in the same step). `fresh` means: even though
 * this node may sit directly downstream of another node using the same
 * `Tool`, treat this as a different physical instance, not a continuation
 * of it — see receptari.model.recipe.RecipeTool's doc comment.
 */
export interface ToolInfo {
  tool: Tool;
  description?: string;
  fresh?: boolean;
  hidden?: boolean;
  counts?: boolean;
}

/** Symmetric with IngredientWithUsage/TransformationWithUsage. */
export interface ToolWithUsage {
  tool: Tool;
  recipeCount: number;
}

export interface ToolUpdate {
  name?: string;
  description?: string;
  defaultHidden?: boolean;
  defaultCounts?: boolean;
}

/** Merge one or more source ingredients/transformations/tools into targetId. */
export interface MergeRequest {
  sourceIds: string[];
  targetId: string;
}

export interface DisplayData {
  position: {
    x: number;
    y: number;
  };
}

export interface RecipeNode {
  id: string;
  name?: string;
  description?: string;
  imageUrl?: string;
  ingredientInfo?: IngredientInfo;
  transformationInfos?: TransformationInfo[];
  toolInfos?: ToolInfo[];
  children: string[];
  displayData?: DisplayData;
  autoFold?: boolean;
  hidden?: boolean;
  /** This node's explicit position in the recipe's flat preparation-step
   *  sequence (buildPreparationSteps() just sorts by this — see
   *  stepOrder.ts for the cascade-repair algorithm that keeps it
   *  consistent with the dependency DAG as steps get dragged to
   *  reorder). Required, not sparse/optional like the old `priority`
   *  field it replaces — every node-creation path must assign one. */
  stepOrder: number;
  recipeId?: string;
}

export interface RecipeGraph {
  rootNodeIds: string[];
  nodes: Record<string, RecipeNode>;
}

export interface Recipe {
  id: string;
  graph: RecipeGraph;
  title: string;
  description?: string;
  quantity?: Quantity;
  mainRootIndex?: number;
}

export interface RecipeCreate {
  name: string;
  description?: string;
  ingredientId?: string;
  quantity?: Quantity;
}

// New RecipeListItem — lightweight recipe for browse/search listings
export interface RecipeListItem {
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  quantity?: Quantity;
  mainRootNodeId: string;
  rootNodeIds: string[];
  mainRootIndex: number;
  updatedAt?: string;
}

// New SubgraphResponse — lazy-load result for expand
export interface SubgraphResponse {
  root: RecipeNode;
  nodes: Record<string, RecipeNode>;
}

// Update types matching backend Pydantic models
export interface RecipeUpdate {
  title?: string;
  name?: string;
  description?: string;
  nodes?: RecipeNode[];
  quantity?: Quantity | null;
  mainRootIndex?: number | null;
}

export interface IngredientUpdate {
  name?: string;
  description?: string;
}

export interface TransformationUpdate {
  name?: string;
  description?: string;
  /** Seconds. */
  typicalDuration?: number | null;
}

export interface UnitDef {
  id: string;
  singular: string;
  plural: string;
  abbreviation?: string;
}

export interface UnitConversion {
  id: string;
  fromUnitId: string;
  toUnitId: string;
  factor: number;
}

// ── Ingredient Recipe Subgraph types ──

export interface IngredientRecipeSubgraphNode {
  id: string;
  name?: string;
  description?: string;
  imageUrl?: string;
  ingredientInfo?: IngredientInfo;
  transformationInfos?: TransformationInfo[];
  toolInfos?: ToolInfo[];
  children: string[];
  stepOrder: number;
}

export interface IngredientRecipeSubgraphResult {
  subgraphId: string;
  rootNodeId: string;
  nodes: Record<string, IngredientRecipeSubgraphNode>;
  nodeCount: number;
  ingredientNames: string[];
  transformationNames: string[];
  /** The recipe this subgraph was found in — shown as a header above the elaboration view. */
  recipeId: string;
  recipeTitle: string;
  recipeImageUrl?: string | null;
}

// ── Suggested Transformations — the dual of ingredient recipe subgraphs ──

export interface TransformationSuggestion {
  /** Undefined for a bare "common transformation" fallback entry. */
  ingredient?: Ingredient;
  transformation: Transformation;
  /** Jaccard similarity, undefined for a bare "common transformation" entry. */
  score?: number;
  count: number;
}
